import Phaser from "phaser";
import { Client, Room, Callbacks } from "@colyseus/sdk";
import { supabase } from "./supabase";

/** WebSocket Colyseus (ex. ws://localhost:2567 en local, wss://ton-serveur en prod). */
const COLYSEUS_URL = process.env.COLYSEUS_URL || "ws://127.0.0.1:2567";

const playerSpriteUrl = new URL("../assets/player.png", import.meta.url).toString();
const tilesetUrl = new URL("../assets/tileset.png", import.meta.url).toString();
const mapUrl = new URL("../assets/map.tmj", import.meta.url).toString();
const slimeSpriteUrl = new URL("../assets/slime.png", import.meta.url).toString();
const goblinSpriteUrl = new URL("../assets/goblin.png", import.meta.url).toString();
const ghostSpriteUrl = new URL("../assets/ghost.png", import.meta.url).toString();
const dragonSpriteUrl = new URL("../assets/dragon.png", import.meta.url).toString();
const jeoseungSajaSpriteUrl = new URL("../assets/sprites/npc_jeoseungsaja.png", import.meta.url).toString();

// charIndex spécial pour le sprite Jeoseung Saja
const SAJA_CHAR_INDEX = 8;

const TILE_SIZE = 32;
const MAP_WIDTH = 60;
const MAP_HEIGHT = 60;
const WORLD_WIDTH = MAP_WIDTH * TILE_SIZE;
const WORLD_HEIGHT = MAP_HEIGHT * TILE_SIZE;
const NPC_INTERACT_DIST = 70;

// =====================================================================
// LOADING SCREEN
// =====================================================================

function showLoadingScreen() {
  const el = document.getElementById("loading-screen");
  if (el) {
    el.classList.remove("fading");
    el.classList.add("visible");
    el.style.opacity = "1";
  }
}

function setLoadingProgress(pct: number, status?: string) {
  const fill = document.getElementById("loading-bar-fill");
  const percent = document.getElementById("loading-percent");
  const statusEl = document.getElementById("loading-status");
  if (fill) fill.style.width = `${Math.max(0, Math.min(100, pct))}%`;
  if (percent) percent.textContent = `${Math.round(pct)}%`;
  if (statusEl && status) statusEl.textContent = status;
}

function hideLoadingScreen() {
  const el = document.getElementById("loading-screen");
  if (!el) return;
  el.classList.add("fading");
  setTimeout(() => {
    el.classList.remove("visible");
    el.classList.remove("fading");
    el.style.opacity = "1";
  }, 700);
}

function getCharFrames(charIndex: number) {
  // Le Jeoseung Saja utilise un spritesheet dédié 3x4 (12 frames seulement)
  if (charIndex === SAJA_CHAR_INDEX) {
    return {
      down: [0, 1, 2],
      left: [3, 4, 5],
      right: [6, 7, 8],
      up: [9, 10, 11]
    };
  }
  // Sinon, layout classique du player.png (8 persos sur 2 blocs de 4)
  const colsPerSheet = 12;
  const baseRow = charIndex < 4 ? 0 : 4;
  const baseCol = (charIndex % 4) * 3;
  const startFrame = baseRow * colsPerSheet + baseCol;
  return {
    down: [startFrame, startFrame + 1, startFrame + 2],
    left: [startFrame + colsPerSheet, startFrame + colsPerSheet + 1, startFrame + colsPerSheet + 2],
    right: [startFrame + colsPerSheet * 2, startFrame + colsPerSheet * 2 + 1, startFrame + colsPerSheet * 2 + 2],
    up: [startFrame + colsPerSheet * 3, startFrame + colsPerSheet * 3 + 1, startFrame + colsPerSheet * 3 + 2]
  };
}

// Helper : retourne la clé de spritesheet selon le charIndex
function getCharSpriteKey(charIndex: number): string {
  return charIndex === SAJA_CHAR_INDEX ? "player_saja" : "player";
}

function getMonsterFrames() {
  return { down: [0, 1, 2], left: [3, 4, 5], right: [6, 7, 8], up: [9, 10, 11] };
}

interface PlayerState { charIndex: number; targetX: number; targetY: number; isNPC: boolean; npcId: string; }
interface MonsterState { targetX: number; targetY: number; spriteKey: string; }

class GameScene extends Phaser.Scene {
  client = new Client(COLYSEUS_URL);
  room!: Room;
  playerEntities: { [sessionId: string]: Phaser.GameObjects.Sprite } = {};
  playerLabels: { [sessionId: string]: Phaser.GameObjects.Text } = {};
  playerBubbles: { [sessionId: string]: Phaser.GameObjects.Text } = {};
  playerStates: { [sessionId: string]: PlayerState } = {};
  monsterEntities: { [id: string]: Phaser.GameObjects.Sprite } = {};
  monsterLabels: { [id: string]: Phaser.GameObjects.Text } = {};
  monsterStates: { [id: string]: MonsterState } = {};
  currentPlayer!: Phaser.GameObjects.Sprite;
  myCharIndex = 0;
  inputPayload = { left: false, right: false, up: false, down: false };
  cursorKeys!: Phaser.Types.Input.Keyboard.CursorKeys;
  zqsdKeys!: { Z: Phaser.Input.Keyboard.Key; Q: Phaser.Input.Keyboard.Key; S: Phaser.Input.Keyboard.Key; D: Phaser.Input.Keyboard.Key };
  clickMarker!: Phaser.GameObjects.Arc;
  chatActive = false;
  currentDialogue: { npcId: string; npcName: string; lines: string[]; index: number } | null = null;
  nearbyNPC: string | null = null;
  cardsOpen = false;
  cachedCards: any[] = [];
  statsOpen = false;
  friendsOpen = false;
  pendingRequestsCount = 0;
  contextMenuTargetSessionId: string | null = null;
  // === Combat targeting ===
  targetMonsterId: string | null = null;
  monsterHpBars: { [id: string]: { bg: Phaser.GameObjects.Graphics; fill: Phaser.GameObjects.Graphics } } = {};
  monsterTargetMarker: Phaser.GameObjects.Arc | null = null;
  isAttacking = false; // clic gauche maintenu sur un mob
  lastAttackSent = 0;  // timestamp du dernier message 30 envoyé
  // Cooldowns skills (côté client, juste pour feedback visuel)
  skillCooldowns: { [skillId: string]: number } = {};
  // Mapping slots → skillId
  hotbarSlots: { [slotIndex: number]: { skillId: string; cooldownMs: number } | null } = {
    0: { skillId: "blackball", cooldownMs: 3000 },
    1: null, 2: null, 3: null, 4: null, 5: null, 6: null, 7: null
  };

  constructor() { super("GameScene"); }

  preload() {
    setLoadingProgress(15, "Chargement des sprites...");
    this.load.spritesheet("player", playerSpriteUrl, { frameWidth: 48, frameHeight: 48 });
    this.load.spritesheet("player_saja", jeoseungSajaSpriteUrl, { frameWidth: 128, frameHeight: 160 });
    this.load.spritesheet("slime", slimeSpriteUrl, { frameWidth: 48, frameHeight: 48 });
    this.load.spritesheet("goblin", goblinSpriteUrl, { frameWidth: 48, frameHeight: 48 });
    this.load.spritesheet("ghost", ghostSpriteUrl, { frameWidth: 48, frameHeight: 48 });
    this.load.spritesheet("dragon", dragonSpriteUrl, { frameWidth: 48, frameHeight: 48 });
    this.load.image("tiles", tilesetUrl);
    this.load.tilemapTiledJSON("map", mapUrl);

    // Progression du chargement Phaser : 15% → 50%
    this.load.on("progress", (value: number) => {
      setLoadingProgress(15 + value * 35, "Chargement des assets...");
    });
    this.load.on("complete", () => {
      setLoadingProgress(50, "Connexion au royaume...");
    });
  }

  create() {
    const map = this.make.tilemap({ key: "map" });
    const tileset = map.addTilesetImage("tileset", "tiles");
    if (tileset) {
      map.createLayer("ground", tileset, 0, 0);
      map.createLayer("objects", tileset, 0, 0);
    }

    this.cameras.main.setBounds(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    this.cameras.main.setRoundPixels(true);
    this.physics.world.setBounds(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    this.cursorKeys = this.input.keyboard!.createCursorKeys();
    this.zqsdKeys = {
      Z: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.Z),
      Q: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.Q),
      S: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      D: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.D)
    };
    this.input.mouse!.disableContextMenu();

    for (let i = 0; i < 8; i++) {
      const f = getCharFrames(i);
      this.anims.create({ key: `walk-down-${i}`, frames: this.anims.generateFrameNumbers("player", { frames: [f.down[0], f.down[1], f.down[2], f.down[1]] }), frameRate: 8, repeat: -1 });
      this.anims.create({ key: `walk-left-${i}`, frames: this.anims.generateFrameNumbers("player", { frames: [f.left[0], f.left[1], f.left[2], f.left[1]] }), frameRate: 8, repeat: -1 });
      this.anims.create({ key: `walk-right-${i}`, frames: this.anims.generateFrameNumbers("player", { frames: [f.right[0], f.right[1], f.right[2], f.right[1]] }), frameRate: 8, repeat: -1 });
      this.anims.create({ key: `walk-up-${i}`, frames: this.anims.generateFrameNumbers("player", { frames: [f.up[0], f.up[1], f.up[2], f.up[1]] }), frameRate: 8, repeat: -1 });
    }

    // Animations pour le Jeoseung Saja (charIndex 8, spritesheet dédié)
    {
      const f = getCharFrames(SAJA_CHAR_INDEX);
      this.anims.create({ key: `walk-down-${SAJA_CHAR_INDEX}`, frames: this.anims.generateFrameNumbers("player_saja", { frames: [f.down[0], f.down[1], f.down[2], f.down[1]] }), frameRate: 6, repeat: -1 });
      this.anims.create({ key: `walk-left-${SAJA_CHAR_INDEX}`, frames: this.anims.generateFrameNumbers("player_saja", { frames: [f.left[0], f.left[1], f.left[2], f.left[1]] }), frameRate: 6, repeat: -1 });
      this.anims.create({ key: `walk-right-${SAJA_CHAR_INDEX}`, frames: this.anims.generateFrameNumbers("player_saja", { frames: [f.right[0], f.right[1], f.right[2], f.right[1]] }), frameRate: 6, repeat: -1 });
      this.anims.create({ key: `walk-up-${SAJA_CHAR_INDEX}`, frames: this.anims.generateFrameNumbers("player_saja", { frames: [f.up[0], f.up[1], f.up[2], f.up[1]] }), frameRate: 6, repeat: -1 });
    }

    const sf = getMonsterFrames();
    this.anims.create({ key: "slime-walk-down", frames: this.anims.generateFrameNumbers("slime", { frames: [sf.down[0], sf.down[1], sf.down[2], sf.down[1]] }), frameRate: 6, repeat: -1 });
    this.anims.create({ key: "slime-walk-left", frames: this.anims.generateFrameNumbers("slime", { frames: [sf.left[0], sf.left[1], sf.left[2], sf.left[1]] }), frameRate: 6, repeat: -1 });
    this.anims.create({ key: "slime-walk-right", frames: this.anims.generateFrameNumbers("slime", { frames: [sf.right[0], sf.right[1], sf.right[2], sf.right[1]] }), frameRate: 6, repeat: -1 });
    this.anims.create({ key: "slime-walk-up", frames: this.anims.generateFrameNumbers("slime", { frames: [sf.up[0], sf.up[1], sf.up[2], sf.up[1]] }), frameRate: 6, repeat: -1 });
    this.anims.create({ key: "goblin-walk-down", frames: this.anims.generateFrameNumbers("goblin", { frames: [sf.down[0], sf.down[1], sf.down[2], sf.down[1]] }), frameRate: 8, repeat: -1 });
    this.anims.create({ key: "goblin-walk-left", frames: this.anims.generateFrameNumbers("goblin", { frames: [sf.left[0], sf.left[1], sf.left[2], sf.left[1]] }), frameRate: 8, repeat: -1 });
    this.anims.create({ key: "goblin-walk-right", frames: this.anims.generateFrameNumbers("goblin", { frames: [sf.right[0], sf.right[1], sf.right[2], sf.right[1]] }), frameRate: 8, repeat: -1 });
    this.anims.create({ key: "goblin-walk-up", frames: this.anims.generateFrameNumbers("goblin", { frames: [sf.up[0], sf.up[1], sf.up[2], sf.up[1]] }), frameRate: 8, repeat: -1 });
    this.anims.create({ key: "ghost-walk-down", frames: this.anims.generateFrameNumbers("ghost", { frames: [sf.down[0], sf.down[1], sf.down[2], sf.down[1]] }), frameRate: 5, repeat: -1 });
    this.anims.create({ key: "ghost-walk-left", frames: this.anims.generateFrameNumbers("ghost", { frames: [sf.left[0], sf.left[1], sf.left[2], sf.left[1]] }), frameRate: 5, repeat: -1 });
    this.anims.create({ key: "ghost-walk-right", frames: this.anims.generateFrameNumbers("ghost", { frames: [sf.right[0], sf.right[1], sf.right[2], sf.right[1]] }), frameRate: 5, repeat: -1 });
    this.anims.create({ key: "ghost-walk-up", frames: this.anims.generateFrameNumbers("ghost", { frames: [sf.up[0], sf.up[1], sf.up[2], sf.up[1]] }), frameRate: 5, repeat: -1 });
    this.anims.create({ key: "dragon-walk-down", frames: this.anims.generateFrameNumbers("dragon", { frames: [sf.down[0], sf.down[1], sf.down[2], sf.down[1]] }), frameRate: 6, repeat: -1 });
    this.anims.create({ key: "dragon-walk-left", frames: this.anims.generateFrameNumbers("dragon", { frames: [sf.left[0], sf.left[1], sf.left[2], sf.left[1]] }), frameRate: 6, repeat: -1 });
    this.anims.create({ key: "dragon-walk-right", frames: this.anims.generateFrameNumbers("dragon", { frames: [sf.right[0], sf.right[1], sf.right[2], sf.right[1]] }), frameRate: 6, repeat: -1 });
    this.anims.create({ key: "dragon-walk-up", frames: this.anims.generateFrameNumbers("dragon", { frames: [sf.up[0], sf.up[1], sf.up[2], sf.up[1]] }), frameRate: 6, repeat: -1 });

    this.clickMarker = this.add.circle(0, 0, 6, 0xffff00, 0.6);
    this.clickMarker.setVisible(false);
    this.clickMarker.setDepth(1000);

    // Marqueur de cible verrouillée (cercle rouge sous le monstre ciblé)
    this.monsterTargetMarker = this.add.circle(0, 0, 24, 0xff3030, 0.4);
    this.monsterTargetMarker.setStrokeStyle(2, 0xff3030, 0.9);
    this.monsterTargetMarker.setVisible(false);
    this.monsterTargetMarker.setDepth(50);

    this.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      if (!this.room || this.chatActive || this.cardsOpen || this.friendsOpen || this.statsOpen) return;
      const x = pointer.worldX;
      const y = pointer.worldY;
      console.log("[CLICK]", { x, y, isAttacking: this.isAttacking, target: this.targetMonsterId, monsterAtPos: this.findMonsterAtPosition(x, y) });

      // Clic droit (bouton 2) → menu contextuel sur joueur
      if (pointer.rightButtonDown()) {
        const clickedPlayer = this.findPlayerAtPosition(x, y);
        if (clickedPlayer && clickedPlayer !== this.room.sessionId) {
          const evt = pointer.event as MouseEvent;
          this.showContextMenu(evt.clientX, evt.clientY, clickedPlayer);
        }
        return;
      }

      // Clic gauche sur un monstre → cible verrouillée + auto-attack
      const clickedMonster = this.findMonsterAtPosition(x, y);
      if (clickedMonster) {
        this.setTarget(clickedMonster);
        this.isAttacking = true;
        this.lastAttackSent = 0; // forcer envoi immédiat
        return;
      }

      const clickedNPC = this.findNPCAtPosition(x, y);
      if (clickedNPC) { this.tryTalkToNPC(clickedNPC); return; }
      if (this.currentDialogue) { this.advanceDialogue(); return; }

      // Clic dans le vide → désélectionne la cible et déplacement
      this.setTarget(null);
      this.room.send(1, { x, y });
      this.clickMarker.setPosition(x, y);
      this.clickMarker.setVisible(true);
      this.tweens.add({
        targets: this.clickMarker, alpha: 0, duration: 500,
        onComplete: () => { this.clickMarker.setVisible(false); this.clickMarker.setAlpha(0.6); }
      });
    });

    // Relâcher le clic → arrêter l'auto-attack
    this.input.on("pointerup", () => {
      this.isAttacking = false;
    });

    // Curseur épée au hover sur un monstre
    this.input.on("pointermove", (pointer: Phaser.Input.Pointer) => {
      if (this.cardsOpen || this.friendsOpen || this.statsOpen) {
        this.game.canvas.style.cursor = "default";
        return;
      }
      const monsterAtCursor = this.findMonsterAtPosition(pointer.worldX, pointer.worldY);
      if (monsterAtCursor) {
        this.game.canvas.style.cursor = "crosshair";
      } else {
        this.game.canvas.style.cursor = "default";
      }
    });

    // Désactiver le menu contextuel par défaut du navigateur sur le canvas
    this.game.canvas.addEventListener("contextmenu", (e) => e.preventDefault());

    this.setupChat();
    this.setupDialogueInput();
    this.setupCardsUI();
    console.log("Connexion au serveur...");
    this.connect();
  }

  setupChat() {
    const chatInput = document.getElementById("chat-input") as HTMLInputElement;
    const chatContainer = document.getElementById("chat-input-container") as HTMLDivElement;
    const chatHint = document.getElementById("chat-hint") as HTMLDivElement;
    document.addEventListener("keydown", (e) => {
      if ((e.key === "i" || e.key === "I") && !this.chatActive && !this.currentDialogue) {
        e.preventDefault();
        if (this.cardsOpen) this.closeCards();
        else this.openCards();
        return;
      }
      if ((e.key === "f" || e.key === "F") && !this.chatActive && !this.currentDialogue) {
        e.preventDefault();
        if (this.friendsOpen) this.closeFriends();
        else this.openFriends();
        return;
      }
      if (e.key === "Tab" && !this.chatActive && !this.currentDialogue) {
        e.preventDefault();
        if (this.statsOpen) this.closeStats();
        else this.openStats();
        return;
      }
      // F1 = alias du slot 1 (boule noire)
      if (e.key === "F1" && !this.chatActive && !this.currentDialogue && !this.cardsOpen && !this.statsOpen && !this.friendsOpen) {
        e.preventDefault();
        this.activateHotbarSlot(0);
        return;
      }
      // Touches AZERTY non-shift de la rangée du haut → slots 0-7
      if (!this.chatActive && !this.currentDialogue && !this.cardsOpen && !this.statsOpen && !this.friendsOpen) {
        const slotKeys: { [k: string]: number } = {
          "&": 0, "é": 1, "\"": 2, "'": 3, "(": 4, "-": 5, "è": 6, "_": 7
        };
        if (slotKeys[e.key] !== undefined) {
          e.preventDefault();
          this.activateHotbarSlot(slotKeys[e.key]);
          return;
        }
      }
      if (e.key === "Escape" && this.statsOpen) { this.closeStats(); return; }
      if (e.key === "Escape" && this.friendsOpen) { this.closeFriends(); return; }
      if (e.key === "Escape" && this.cardsOpen) { this.closeCards(); return; }
      if (this.cardsOpen) return;
      if (this.currentDialogue && e.key === " ") { e.preventDefault(); this.advanceDialogue(); return; }
      if (e.key === "Enter") {
        if (this.currentDialogue) return;
        if (!this.chatActive) {
          this.chatActive = true;
          chatContainer.style.display = "flex";
          chatHint.style.display = "none";
          chatInput.value = "";
          chatInput.focus();
          this.input.keyboard?.disableGlobalCapture();
        } else {
          const text = chatInput.value.trim();
          if (text && this.room) {
            // Commandes spéciales
            if (text.startsWith("/ami ") || text.startsWith("/addfriend ")) {
              const pseudo = text.replace(/^\/(ami|addfriend)\s+/, "").trim();
              if (pseudo) this.sendFriendRequestByPseudo(pseudo);
            } else {
              this.room.send(2, { text });
            }
          }
          this.closeChat();
        }
      } else if (e.key === "Escape") {
        if (this.chatActive) this.closeChat();
        if (this.currentDialogue) this.closeDialogue();
      } else if (e.key === " " && !this.chatActive && !this.currentDialogue) {
        if (this.nearbyNPC) { e.preventDefault(); this.tryTalkToNPC(this.nearbyNPC); }
      }
    });
  }

  setupDialogueInput() {
    const dialogueBox = document.getElementById("dialogue-box");
    if (dialogueBox) dialogueBox.addEventListener("click", () => this.advanceDialogue());
  }

  setupCardsUI() {
    document.getElementById("cards-button")?.addEventListener("click", () => this.openCards());
    document.getElementById("cards-close")?.addEventListener("click", () => this.closeCards());
    document.getElementById("stats-close")?.addEventListener("click", () => this.closeStats());
    document.getElementById("friends-button")?.addEventListener("click", () => this.openFriends());
    document.getElementById("friends-close")?.addEventListener("click", () => this.closeFriends());
    document.getElementById("friend-search-button")?.addEventListener("click", () => this.sendFriendRequestFromInput());

    document.getElementById("friend-search-input")?.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "Enter") this.sendFriendRequestFromInput();
    });

    // Menu contextuel : clic ailleurs ferme
    document.addEventListener("click", () => this.hideContextMenu());

    // Action "Ajouter en ami" dans le menu contextuel
    document.getElementById("ctx-add-friend")?.addEventListener("click", () => {
      if (!this.contextMenuTargetSessionId) return;
      const targetState = this.playerStates[this.contextMenuTargetSessionId];
      if (!targetState || targetState.isNPC) return;
      const targetPseudo = this.playerLabels[this.contextMenuTargetSessionId]?.text;
      if (!targetPseudo) return;
      this.sendFriendRequestByPseudo(targetPseudo);
      this.hideContextMenu();
    });

    // Hotbar : clic sur slot pour activer le skill
    document.querySelectorAll(".hotbar-slot").forEach((slot) => {
      slot.addEventListener("click", (e) => {
        e.stopPropagation();
        const slotIdx = parseInt((slot as HTMLElement).dataset.slot || "-1");
        if (slotIdx >= 0) this.activateHotbarSlot(slotIdx);
      });
    });

    // Hotbar : drag & drop entre slots
    let draggedSlotIdx: number | null = null;
    let dragGhost: HTMLElement | null = null;
    let dragStartX = 0;
    let dragStartY = 0;
    let dragMoved = false;

    document.querySelectorAll(".hotbar-slot").forEach((slot) => {
      const slotEl = slot as HTMLElement;

      slotEl.addEventListener("mousedown", (e) => {
        const idx = parseInt(slotEl.dataset.slot || "-1");
        if (idx < 0) return;
        // On ne drag que les slots non vides
        if (!this.hotbarSlots[idx]) return;
        draggedSlotIdx = idx;
        dragStartX = e.clientX;
        dragStartY = e.clientY;
        dragMoved = false;
      });
    });

    document.addEventListener("mousemove", (e) => {
      if (draggedSlotIdx === null) return;
      const dx = e.clientX - dragStartX;
      const dy = e.clientY - dragStartY;
      // Activer le drag uniquement après un déplacement de 5px (sinon c'est un clic)
      if (!dragMoved && Math.sqrt(dx * dx + dy * dy) < 5) return;
      dragMoved = true;
      // Créer le ghost si pas encore fait
      if (!dragGhost) {
        const sourceEl = document.querySelector(`.hotbar-slot[data-slot="${draggedSlotIdx}"]`);
        if (!sourceEl) return;
        dragGhost = sourceEl.cloneNode(true) as HTMLElement;
        dragGhost.style.position = "fixed";
        dragGhost.style.pointerEvents = "none";
        dragGhost.style.opacity = "0.85";
        dragGhost.style.zIndex = "9999";
        dragGhost.style.transform = "scale(1.1)";
        dragGhost.style.boxShadow = "0 4px 16px rgba(139, 63, 219, 0.6)";
        document.body.appendChild(dragGhost);
        // Marquer le slot source comme étant en cours de drag
        (sourceEl as HTMLElement).style.opacity = "0.3";
      }
      dragGhost.style.left = `${e.clientX - 26}px`;
      dragGhost.style.top = `${e.clientY - 26}px`;
    });

    document.addEventListener("mouseup", (e) => {
      if (draggedSlotIdx === null) return;
      // Si on n'a pas dépassé le seuil, c'est un clic — déjà géré par le click handler
      if (!dragMoved) {
        draggedSlotIdx = null;
        return;
      }
      // Trouver le slot cible sous le curseur
      let targetSlotIdx: number | null = null;
      const elementsUnder = document.elementsFromPoint(e.clientX, e.clientY);
      for (const el of elementsUnder) {
        const slotEl = (el as HTMLElement).closest(".hotbar-slot") as HTMLElement | null;
        if (slotEl) {
          targetSlotIdx = parseInt(slotEl.dataset.slot || "-1");
          break;
        }
      }
      // Swap si cible valide et différente
      if (targetSlotIdx !== null && targetSlotIdx >= 0 && targetSlotIdx !== draggedSlotIdx) {
        const tmp = this.hotbarSlots[draggedSlotIdx];
        this.hotbarSlots[draggedSlotIdx] = this.hotbarSlots[targetSlotIdx];
        this.hotbarSlots[targetSlotIdx] = tmp;
        this.renderHotbar();
        this.saveHotbar();
      }
      // Cleanup
      const sourceEl = document.querySelector(`.hotbar-slot[data-slot="${draggedSlotIdx}"]`);
      if (sourceEl) (sourceEl as HTMLElement).style.opacity = "";
      if (dragGhost) {
        dragGhost.remove();
        dragGhost = null;
      }
      draggedSlotIdx = null;
      dragMoved = false;
    });

    // Cancel drag si on quitte la fenêtre
    document.addEventListener("mouseleave", () => {
      if (draggedSlotIdx === null) return;
      const sourceEl = document.querySelector(`.hotbar-slot[data-slot="${draggedSlotIdx}"]`);
      if (sourceEl) (sourceEl as HTMLElement).style.opacity = "";
      if (dragGhost) {
        dragGhost.remove();
        dragGhost = null;
      }
      draggedSlotIdx = null;
      dragMoved = false;
    });
  }

  openCards() {
    if (!this.room) return;
    this.cardsOpen = true;
    document.getElementById("cards-overlay")!.style.display = "flex";
    this.room.send(11);
  }

  closeCards() {
    this.cardsOpen = false;
    document.getElementById("cards-overlay")!.style.display = "none";
  }

  openStats() {
    if (!this.room) return;
    this.statsOpen = true;
    document.getElementById("stats-overlay")!.style.display = "flex";
    document.getElementById("stats-content")!.innerHTML = '<div style="text-align:center;color:#888;padding:40px;">Chargement...</div>';
    (this.room as any).send(14);
  }

  closeStats() {
    this.statsOpen = false;
    document.getElementById("stats-overlay")!.style.display = "none";
  }

  // ==================== AMIS ====================

  openFriends() {
    if (!this.room) return;
    this.friendsOpen = true;
    document.getElementById("friends-overlay")!.style.display = "flex";
    (this.room as any).send(20);
  }

  closeFriends() {
    this.friendsOpen = false;
    document.getElementById("friends-overlay")!.style.display = "none";
  }

  sendFriendRequestFromInput() {
    const input = document.getElementById("friend-search-input") as HTMLInputElement;
    const pseudo = input.value.trim();
    if (!pseudo) return;
    this.sendFriendRequestByPseudo(pseudo);
    input.value = "";
  }

  sendFriendRequestByPseudo(pseudo: string) {
    if (!this.room) return;
    (this.room as any).send(21, { pseudo });
  }

  acceptFriendRequest(friendshipId: string) {
    if (!this.room) return;
    (this.room as any).send(22, { friendshipId });
  }

  removeFriendship(friendshipId: string) {
    if (!this.room) return;
    (this.room as any).send(23, { friendshipId });
  }

  renderFriends(data: { friends: any[]; pending: { received: any[]; sent: any[] } }) {
    const friends = data.friends || [];
    const received = data.pending?.received || [];
    const sent = data.pending?.sent || [];

    document.getElementById("friends-received-count")!.textContent = String(received.length);
    document.getElementById("friends-sent-count")!.textContent = String(sent.length);
    document.getElementById("friends-list-count")!.textContent = String(friends.length);

    // Demandes reçues
    const receivedEl = document.getElementById("friends-received-list")!;
    if (received.length === 0) {
      receivedEl.innerHTML = '<div class="friends-empty">Aucune demande reçue</div>';
    } else {
      receivedEl.innerHTML = received.map(r => `
        <div class="friend-row">
          <div class="friend-info">
            <span class="friend-name">${escapeHtml(r.pseudo)}</span>
            <span class="friend-meta">vous a envoyé une demande</span>
          </div>
          <div class="friend-actions">
            <button class="friend-btn accept" data-fid="${r.friendshipId}" data-action="accept">✓ Accepter</button>
            <button class="friend-btn refuse" data-fid="${r.friendshipId}" data-action="refuse">✗ Refuser</button>
          </div>
        </div>
      `).join("");
    }

    // Demandes envoyées
    const sentEl = document.getElementById("friends-sent-list")!;
    if (sent.length === 0) {
      sentEl.innerHTML = '<div class="friends-empty">Aucune demande envoyée</div>';
    } else {
      sentEl.innerHTML = sent.map(s => `
        <div class="friend-row">
          <div class="friend-info">
            <span class="friend-name">${escapeHtml(s.pseudo)}</span>
            <span class="friend-meta">en attente de réponse</span>
          </div>
          <div class="friend-actions">
            <button class="friend-btn cancel" data-fid="${s.friendshipId}" data-action="cancel">Annuler</button>
          </div>
        </div>
      `).join("");
    }

    // Liste d'amis
    const listEl = document.getElementById("friends-list")!;
    if (friends.length === 0) {
      listEl.innerHTML = '<div class="friends-empty">Aucun ami pour le moment</div>';
    } else {
      listEl.innerHTML = friends.map(f => `
        <div class="friend-row">
          <div class="friend-info">
            <span class="friend-status-dot ${f.isOnline ? 'online' : ''}"></span>
            <span class="friend-name">${escapeHtml(f.pseudo)}</span>
            <span class="friend-meta">${f.isOnline ? 'En ligne' : 'Hors ligne'}</span>
          </div>
          <div class="friend-actions">
            <button class="friend-btn remove" data-fid="${f.friendshipId}" data-action="remove">Supprimer</button>
          </div>
        </div>
      `).join("");
    }

    // Attacher les handlers sur les boutons
    document.querySelectorAll(".friend-btn").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const target = e.currentTarget as HTMLButtonElement;
        const fid = target.dataset.fid!;
        const action = target.dataset.action!;
        if (action === "accept") this.acceptFriendRequest(fid);
        else this.removeFriendship(fid); // refuse, cancel, remove
      });
    });

    // Mettre à jour la pastille
    this.pendingRequestsCount = received.length;
    this.updateFriendsBadge();
  }

  updateFriendsBadge() {
    const badge = document.getElementById("friends-badge")!;
    if (this.pendingRequestsCount > 0) {
      badge.textContent = String(this.pendingRequestsCount);
      badge.style.display = "flex";
    } else {
      badge.style.display = "none";
    }
  }

  showToast(message: string, kind: "ok" | "error" | "neutral" = "neutral") {
    const toast = document.getElementById("toast-notif")!;
    toast.textContent = message;
    toast.className = "";
    if (kind === "ok") toast.classList.add("ok");
    else if (kind === "error") toast.classList.add("error");
    toast.style.display = "block";
    setTimeout(() => { toast.style.display = "none"; }, 4000);
  }

  showContextMenu(x: number, y: number, sessionId: string) {
    this.contextMenuTargetSessionId = sessionId;
    const menu = document.getElementById("player-context-menu")!;
    menu.style.left = `${x}px`;
    menu.style.top = `${y}px`;
    menu.style.display = "block";
  }

  hideContextMenu() {
    this.contextMenuTargetSessionId = null;
    document.getElementById("player-context-menu")!.style.display = "none";
  }

  renderStats(profile: any) {
    const winrate = profile.totalBattles > 0
      ? Math.round((profile.totalCaptures + (profile.totalBattles - profile.totalCaptures - profile.totalDefeats - profile.totalFlees)) / profile.totalBattles * 100)
      : 0;
    const captureRate = profile.totalBattles > 0
      ? Math.round(profile.totalCaptures / profile.totalBattles * 100)
      : 0;

    const hpPct = profile.maxHp > 0 ? Math.max(0, Math.min(100, (profile.hp / profile.maxHp) * 100)) : 0;

    const xpPct = profile.xpForNext === Infinity || profile.level >= 60
      ? 100
      : Math.max(0, Math.min(100, (profile.xp / profile.xpForNext) * 100));

    const xpText = profile.level >= 60 ? "MAX" : `${profile.xp} / ${profile.xpForNext}`;

    const memberSince = profile.createdAt ? new Date(profile.createdAt).toLocaleDateString("fr-FR") : "?";

    document.getElementById("stats-content")!.innerHTML = `
      <div class="stats-section">
        <div class="stats-section-title">Identité</div>
        <div class="stats-row"><span class="label">Pseudo</span><span class="value">${escapeHtml(profile.pseudo)}</span></div>
        <div class="stats-row"><span class="label">Rôle</span><span class="value"><span class="role-badge role-${profile.role}">${profile.role}</span></span></div>
        <div class="stats-row"><span class="label">Membre depuis</span><span class="value">${memberSince}</span></div>
        <div class="stats-row"><span class="label">Map actuelle</span><span class="value">${escapeHtml(profile.currentMapId || "—")}</span></div>
      </div>

      <div class="stats-section">
        <div class="stats-section-title">Niveau</div>
        <div class="stats-row highlight"><span class="label">Niveau actuel</span><span class="value">${profile.level} / 60</span></div>
        <div class="stats-bar-container">
          <div class="stats-bar-fill xp" style="width: ${xpPct}%;"></div>
          <div class="stats-bar-text">XP : ${xpText}</div>
        </div>
      </div>

      <div class="stats-section">
        <div class="stats-section-title">Points de vie</div>
        <div class="stats-bar-container">
          <div class="stats-bar-fill hp" style="width: ${hpPct}%;"></div>
          <div class="stats-bar-text">${profile.hp} / ${profile.maxHp} PV</div>
        </div>
      </div>

      <div class="stats-section">
        <div class="stats-section-title">Statistiques de combat</div>
        <div class="stats-row"><span class="label">Combats totaux</span><span class="value">${profile.totalBattles}</span></div>
        <div class="stats-row"><span class="label">Captures réussies</span><span class="value">${profile.totalCaptures}</span></div>
        <div class="stats-row"><span class="label">Défaites</span><span class="value">${profile.totalDefeats}</span></div>
        <div class="stats-row"><span class="label">Fuites</span><span class="value">${profile.totalFlees}</span></div>
        <div class="stats-row highlight"><span class="label">Taux de capture</span><span class="value">${captureRate}%</span></div>
        <div class="stats-row highlight"><span class="label">Taux de réussite global</span><span class="value">${winrate}%</span></div>
      </div>

      <div class="stats-section">
        <div class="stats-section-title">Collection</div>
        <div class="stats-row"><span class="label">Créatures uniques</span><span class="value">${profile.uniqueCards}</span></div>
        <div class="stats-row"><span class="label">Total de cartes</span><span class="value">${profile.totalCards}</span></div>
      </div>
    `;
  }

  showLevelUp(newLevel: number) {
    const notif = document.getElementById("levelup-notif")!;
    document.getElementById("levelup-notif-level")!.textContent = `Niveau ${newLevel}`;
    notif.style.display = "block";
    notif.style.animation = "none";
    void (notif as HTMLElement).offsetWidth;
    notif.style.animation = "pop 0.5s ease-out";
    setTimeout(() => { notif.style.display = "none"; }, 3500);
  }

  renderCards(cards: any[]) {
    this.cachedCards = cards;
    const grid = document.getElementById("cards-grid")!;
    const countEl = document.getElementById("cards-count")!;
    grid.innerHTML = "";

    if (cards.length === 0) {
      const empty = document.createElement("div");
      empty.className = "card-empty";
      empty.textContent = "Ta collection est vide.";
      grid.appendChild(empty);
      countEl.textContent = "0 cartes";
      return;
    }

    let totalCount = 0;
    for (const c of cards) {
      totalCount += c.count;
      const div = document.createElement("div");
      div.className = `card rarity-${c.rarity}`;
      const imgUrl =
        c.spriteKey === "slime" ? slimeSpriteUrl :
        c.spriteKey === "goblin" ? goblinSpriteUrl :
        c.spriteKey === "ghost" ? ghostSpriteUrl :
        c.spriteKey === "dragon" ? dragonSpriteUrl : "";
      div.innerHTML = `
        <div class="card-count">×${c.count}</div>
        <div class="card-img"><div style="background-image:url('${imgUrl}'); background-position:-48px 0; background-size:auto; background-repeat:no-repeat; width:48px; height:48px; image-rendering:pixelated; transform:scale(1.4); transform-origin:center;"></div></div>
        <div class="card-name">${c.name}</div>
        <div class="card-stats">
          <span>⚔️ ${c.attack}</span>
          <span>🛡️ ${c.defense}</span>
          <span>❤️ ${c.maxHp}</span>
        </div>
        <div class="card-rarity" style="color:${this.rarityColor(c.rarity)}">${c.rarity}</div>
      `;
      grid.appendChild(div);
    }
    const uniqueCount = cards.length;
    countEl.textContent = `${uniqueCount} carte(s) unique(s) · ${totalCount} au total`;
  }

  rarityColor(rarity: string): string {
    switch (rarity) {
      case "common": return "#95a5a6";
      case "uncommon": return "#2ecc71";
      case "rare": return "#3498db";
      case "epic": return "#9b59b6";
      case "legendary": return "#f1c40f";
      default: return "#fff";
    }
  }

  closeChat() {
    const chatInput = document.getElementById("chat-input") as HTMLInputElement;
    const chatContainer = document.getElementById("chat-input-container") as HTMLDivElement;
    const chatHint = document.getElementById("chat-hint") as HTMLDivElement;
    this.chatActive = false;
    chatInput.blur();
    chatContainer.style.display = "none";
    chatHint.style.display = "block";
    this.input.keyboard?.enableGlobalCapture();
  }

  findNPCAtPosition(x: number, y: number): string | null {
    for (const sid in this.playerStates) {
      const state = this.playerStates[sid];
      if (!state.isNPC) continue;
      const entity = this.playerEntities[sid];
      if (!entity) continue;
      const dx = entity.x - x;
      const dy = entity.y - y;
      if (Math.abs(dx) < 24 && dy > -48 && dy < 5) return sid;
    }
    return null;
  }

  findPlayerAtPosition(x: number, y: number): string | null {
    let bestSid: string | null = null;
    let bestDist = Infinity;
    for (const sid in this.playerStates) {
      const state = this.playerStates[sid];
      if (state.isNPC) continue;
      const entity = this.playerEntities[sid];
      if (!entity) continue;
      // Hitbox plus large : 24x48 centrée sur le sprite (origine Y aux pieds)
      const dx = entity.x - x;
      const dy = (entity.y - 24) - y; // centre vertical du sprite
      if (Math.abs(dx) < 24 && Math.abs(dy) < 28) {
        const dist = dx * dx + dy * dy;
        if (dist < bestDist) {
          bestDist = dist;
          bestSid = sid;
        }
      }
    }
    return bestSid;
  }

  findMonsterAtPosition(x: number, y: number): string | null {
    let bestId: string | null = null;
    let bestDist = Infinity;
    for (const id in this.monsterEntities) {
      const entity = this.monsterEntities[id];
      if (!entity || !entity.visible) continue;
      // Hitbox 32x40 centrée sur le sprite (origine Y aux pieds)
      const dx = entity.x - x;
      const dy = (entity.y - 20) - y;
      if (Math.abs(dx) < 24 && Math.abs(dy) < 24) {
        const dist = dx * dx + dy * dy;
        if (dist < bestDist) {
          bestDist = dist;
          bestId = id;
        }
      }
    }
    return bestId;
  }

  setTarget(monsterId: string | null) {
    this.targetMonsterId = monsterId;
    if (monsterId) {
      const entity = this.monsterEntities[monsterId];
      if (entity && this.monsterTargetMarker) {
        this.monsterTargetMarker.setPosition(entity.x, entity.y - 4);
        this.monsterTargetMarker.setVisible(true);
      }
    } else if (this.monsterTargetMarker) {
      this.monsterTargetMarker.setVisible(false);
    }
  }

  castSkill(skillId: string) {
    if (!this.room) return;
    if (!this.targetMonsterId) {
      this.showToast("Aucune cible verrouillée", "error");
      return;
    }
    // Check cooldown côté client (anti-spam de messages)
    const now = Date.now();
    const cooldowns: { [k: string]: number } = { blackball: 3000 };
    const lastCast = this.skillCooldowns[skillId] || 0;
    if (now - lastCast < (cooldowns[skillId] || 1000)) {
      const remaining = Math.ceil(((cooldowns[skillId] || 1000) - (now - lastCast)) / 1000);
      this.showToast(`Cooldown ${remaining}s`, "error");
      return;
    }
    this.skillCooldowns[skillId] = now;
    this.room.send(31, { skillId, targetMonsterId: this.targetMonsterId });
  }

  activateHotbarSlot(slotIndex: number) {
    const slot = this.hotbarSlots[slotIndex];
    if (!slot) {
      this.showToast("Slot vide", "error");
      return;
    }
    this.castSkill(slot.skillId);
  }

  updateHotbarCooldowns() {
    const now = Date.now();
    for (const slotIndexStr in this.hotbarSlots) {
      const slotIndex = parseInt(slotIndexStr);
      const slotDef = this.hotbarSlots[slotIndex];
      const slotEl = document.querySelector(`.hotbar-slot[data-slot="${slotIndex}"]`);
      if (!slotEl || !slotDef) continue;
      const lastCast = this.skillCooldowns[slotDef.skillId] || 0;
      const elapsed = now - lastCast;
      const cdMs = slotDef.cooldownMs;
      const cdEl = slotEl.querySelector(".hotbar-cooldown") as HTMLElement | null;
      if (elapsed < cdMs) {
        slotEl.classList.add("on-cooldown");
        if (cdEl) {
          const remaining = Math.ceil((cdMs - elapsed) / 1000);
          cdEl.textContent = String(remaining);
        }
      } else {
        slotEl.classList.remove("on-cooldown");
        if (cdEl) cdEl.textContent = "";
      }
    }
  }

  tryTalkToNPC(npcSessionId: string) {
    if (!this.room || !this.currentPlayer) return;
    const npc = this.playerEntities[npcSessionId];
    if (!npc) return;
    const dx = npc.x - this.currentPlayer.x;
    const dy = npc.y - this.currentPlayer.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > NPC_INTERACT_DIST) return;
    const state = this.playerStates[npcSessionId];
    if (!state || !state.isNPC) return;
    this.room.send(3, { npcId: state.npcId });
  }

  showDialogue(npcName: string, lines: string[]) {
    this.currentDialogue = { npcId: "", npcName, lines, index: 0 };
    document.getElementById("dialogue-name")!.textContent = npcName;
    document.getElementById("dialogue-text")!.textContent = lines[0];
    document.getElementById("dialogue-box")!.style.display = "block";
  }

  advanceDialogue() {
    if (!this.currentDialogue) return;
    this.currentDialogue.index++;
    if (this.currentDialogue.index >= this.currentDialogue.lines.length) { this.closeDialogue(); return; }
    document.getElementById("dialogue-text")!.textContent = this.currentDialogue.lines[this.currentDialogue.index];
  }

  closeDialogue() {
    this.currentDialogue = null;
    document.getElementById("dialogue-box")!.style.display = "none";
  }

  showBubble(sessionId: string, text: string) {
    const old = this.playerBubbles[sessionId];
    if (old) old.destroy();
    const entity = this.playerEntities[sessionId];
    if (!entity) return;
    const state = this.playerStates[sessionId];
    const bubbleOffset = state?.charIndex === SAJA_CHAR_INDEX ? 175 : 60;
    const bubble = this.add.text(entity.x, entity.y - bubbleOffset, text, {
      fontSize: "13px", fontFamily: "Arial", color: "#ffffff",
      backgroundColor: "#000000bb", padding: { x: 6, y: 3 }, align: "center", wordWrap: { width: 200 }
    });
    bubble.setOrigin(0.5, 1);
    bubble.setDepth(102);
    this.playerBubbles[sessionId] = bubble;
    this.time.delayedCall(4000, () => {
      if (this.playerBubbles[sessionId] === bubble) {
        bubble.destroy();
        delete this.playerBubbles[sessionId];
      }
    });
  }

  appendChatHistory(name: string, text: string) {
    const history = document.getElementById("chat-history");
    if (!history) return;
    const line = document.createElement("div");
    line.className = "chat-line";
    line.innerHTML = `<span class="pseudo">${escapeHtml(name)}:</span>${escapeHtml(text)}`;
    history.appendChild(line);
    while (history.children.length > 8) history.removeChild(history.children[0]);
    setTimeout(() => {
      line.style.transition = "opacity 1s";
      line.style.opacity = "0";
      setTimeout(() => line.remove(), 1000);
    }, 10000);
  }

  async connect() {
    try {
      setLoadingProgress(55, "Authentification...");
      // Récupérer le token Supabase de la session active
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        console.error("Pas de token Supabase. Reconnecte-toi.");
        hideLoadingScreen();
        document.getElementById("game-container")!.style.display = "none";
        document.getElementById("auth-screen")!.style.display = "flex";
        return;
      }
      setLoadingProgress(65, "Connexion au serveur...");
      this.room = await this.client.joinOrCreate("my_room", { token });
      console.log("Connecté !");
      setLoadingProgress(80, "Chargement de tes données...");
      const callbacks = Callbacks.get(this.room);

      callbacks.onAdd("players", (player, sessionId) => {
        const p = player as any;
        const sid = sessionId as string;
        const charIndex = p.charIndex || 0;
        const f = getCharFrames(charIndex);
        const spriteKey = getCharSpriteKey(charIndex);
        const entity = this.add.sprite(p.x, p.y, spriteKey, f.down[1]);
        entity.setOrigin(0.5, 1);
        entity.setScale(1);
        entity.setDepth(100);
        this.playerEntities[sid] = entity;
        this.playerStates[sid] = {
          charIndex, targetX: p.x, targetY: p.y, isNPC: !!p.isNPC, npcId: p.npcId || ""
        };

        const isMe = sid === this.room.sessionId;
        if (isMe) this.myCharIndex = charIndex;
        const labelColor = p.isNPC ? "#88e0ff" : (isMe ? "#ffff66" : "#ffffff");
        // Hauteur du label adaptée à la taille du sprite
        const labelOffsetY = charIndex === SAJA_CHAR_INDEX ? 165 : 50;
        const label = this.add.text(p.x, p.y - labelOffsetY, p.name || "?", {
          fontSize: "12px", fontFamily: "Arial", color: labelColor,
          stroke: "#000000", strokeThickness: 3, fontStyle: "bold"
        });
        label.setOrigin(0.5, 0.5);
        label.setDepth(101);
        this.playerLabels[sid] = label;

        if (isMe) {
          this.currentPlayer = entity;
          this.cameras.main.startFollow(entity, true);
        }

        callbacks.onChange(p, () => {
          const state = this.playerStates[sid];
          if (state) { state.targetX = p.x; state.targetY = p.y; }
          if (label.text !== p.name) label.setText(p.name || "?");
          const fc = getCharFrames(charIndex);
          const idleFrames: any = { down: fc.down[1], left: fc.left[1], right: fc.right[1], up: fc.up[1] };

          if (p.moving) {
            entity.anims.play(`walk-${p.dir}-${charIndex}`, true);
          } else {
            entity.anims.stop();
            entity.setFrame(idleFrames[p.dir]);
          }
        });
      });

      callbacks.onRemove("players", (_player, sessionId) => {
        const sid = sessionId as string;
        const entity = this.playerEntities[sid];
        const label = this.playerLabels[sid];
        const bubble = this.playerBubbles[sid];
        if (entity) { entity.destroy(); delete this.playerEntities[sid]; }
        if (label) { label.destroy(); delete this.playerLabels[sid]; }
        if (bubble) { bubble.destroy(); delete this.playerBubbles[sid]; }
        delete this.playerStates[sid];
      });

      callbacks.onAdd("monsters", (monster, monsterId) => {
        const m = monster as any;
        const id = monsterId as string;
        const entity = this.add.sprite(m.x, m.y, m.spriteKey, 1);
        entity.setOrigin(0.5, 1);
        entity.setScale(1);
        entity.setDepth(100);
        this.monsterEntities[id] = entity;
        this.monsterStates[id] = { targetX: m.x, targetY: m.y, spriteKey: m.spriteKey };

        const label = this.add.text(m.x, m.y - 50, m.name, {
          fontSize: "11px", fontFamily: "Arial", color: "#ff8888",
          stroke: "#000000", strokeThickness: 3, fontStyle: "bold"
        });
        label.setOrigin(0.5, 0.5);
        label.setDepth(101);
        this.monsterLabels[id] = label;

        // Barre de HP au-dessus du monstre
        const hpBg = this.add.graphics();
        const hpFill = this.add.graphics();
        hpBg.setDepth(101);
        hpFill.setDepth(102);
        this.monsterHpBars[id] = { bg: hpBg, fill: hpFill };

        callbacks.onChange(m, () => {
          const state = this.monsterStates[id];
          if (state) { state.targetX = m.x; state.targetY = m.y; }
          entity.setVisible(m.alive);
          label.setVisible(m.alive);
          const bars = this.monsterHpBars[id];
          if (bars) {
            bars.bg.setVisible(m.alive);
            bars.fill.setVisible(m.alive);
          }
          // Si le monstre meurt et était la cible, on désélectionne
          if (!m.alive && this.targetMonsterId === id) {
            this.setTarget(null);
          }
          if (m.moving && m.alive) {
            entity.anims.play(`${m.spriteKey}-walk-${m.dir}`, true);
          } else {
            entity.anims.stop();
            const idleFrames: any = { down: 1, left: 4, right: 7, up: 10 };
            entity.setFrame(idleFrames[m.dir] || 1);
          }
        });
      });

      callbacks.onRemove("monsters", (_monster, monsterId) => {
        const id = monsterId as string;
        const entity = this.monsterEntities[id];
        const label = this.monsterLabels[id];
        const bars = this.monsterHpBars[id];
        if (entity) { entity.destroy(); delete this.monsterEntities[id]; }
        if (label) { label.destroy(); delete this.monsterLabels[id]; }
        if (bars) {
          bars.bg.destroy();
          bars.fill.destroy();
          delete this.monsterHpBars[id];
        }
        if (this.targetMonsterId === id) this.setTarget(null);
        delete this.monsterStates[id];
      });

      this.room.onMessage("chat", (msg: any) => {
        this.showBubble(msg.sessionId, msg.text);
        this.appendChatHistory(msg.name, msg.text);
      });
      this.room.onMessage("dialogue", (msg: any) => { this.showDialogue(msg.npcName, msg.lines); });

      this.room.onMessage("cards", (data: any) => {
        this.renderCards(data.cards || []);
      });

      this.room.onMessage("profile", (data: any) => {
        this.renderStats(data);
        // Met aussi à jour la barre XP en haut à gauche
        this.updateXpBar(data.level, data.xp, data.xpForNext);
      });

      this.room.onMessage("levelUp", (data: any) => {
        this.showLevelUp(data.newLevel);
      });

      this.room.onMessage("friends", (data: any) => {
        this.renderFriends(data);
      });

      this.room.onMessage("friendActionResult", (data: any) => {
        const resultEl = document.getElementById("friend-search-result");
        if (resultEl && this.friendsOpen) {
          resultEl.textContent = data.message;
          resultEl.className = data.ok ? "ok" : "err";
        }
        this.showToast(data.message, data.ok ? "ok" : "error");
        // Recharger la liste si action réussie
        if (data.ok) (this.room as any).send(20);
      });

      this.room.onMessage("friendRequestReceived", (data: any) => {
        this.showToast(`👥 ${data.fromPseudo} t'a envoyé une demande d'ami !`, "neutral");
        this.pendingRequestsCount++;
        this.updateFriendsBadge();
      });

      // === COMBAT TEMPS RÉEL ===
      this.room.onMessage("attackHit", (data: any) => {
        this.handleAttackHit(data);
      });

      this.room.onMessage("playerDied", () => {
        this.showToast("💀 Tu es mort. Respawn...", "error");
        this.isAttacking = false;
        this.setTarget(null);
      });

      this.room.onMessage("skillCast", (data: any) => {
        this.handleSkillCast(data);
      });

      this.room.onMessage("skillResult", (data: any) => {
        if (!data.ok && data.message) this.showToast(data.message, "error");
      });

      this.room.onMessage("hotbarConfig", (data: any) => {
        this.applyHotbarConfig(data.slots);
        // Tout est chargé : on cache le loading screen
        setLoadingProgress(100, "Bienvenue dans le royaume...");
        setTimeout(() => hideLoadingScreen(), 400);
      });

      this.room.onMessage("xpGain", (data: any) => {
        if (data.targetSessionId === this.room.sessionId) {
          this.showFloatingDamage(data.monsterX, data.monsterY - 50, data.amount, "#ffd166");
        }
      });

      this.room.onMessage("playerStats", (data: any) => {
        // Mise à jour de la barre XP visible
        this.updateXpBar(data.level, data.xp, data.xpForNext);
      });

      // Demande la config hotbar au serveur
      setLoadingProgress(90, "Chargement de tes sorts...");
      (this.room as any).send(40);
      // Demande aussi le profil pour avoir level/xp/xpForNext
      (this.room as any).send(14);
    } catch (e) {
      console.error("Erreur connexion:", e);
      hideLoadingScreen();
    }
  }

  applyHotbarConfig(slots: { [k: string]: string | null }) {
    // Définition des skills disponibles côté client
    const skillDefs: { [k: string]: { cooldownMs: number } } = {
      blackball: { cooldownMs: 3000 }
    };
    // Reset
    for (let i = 0; i < 8; i++) this.hotbarSlots[i] = null;
    // Apply
    for (const slotIdxStr in slots) {
      const slotIdx = parseInt(slotIdxStr);
      const skillId = slots[slotIdxStr];
      if (skillId && skillDefs[skillId]) {
        this.hotbarSlots[slotIdx] = { skillId, cooldownMs: skillDefs[skillId].cooldownMs };
      }
    }
    this.renderHotbar();
  }

  renderHotbar() {
    for (let i = 0; i < 8; i++) {
      const slotEl = document.querySelector(`.hotbar-slot[data-slot="${i}"]`) as HTMLElement | null;
      if (!slotEl) continue;
      const slotDef = this.hotbarSlots[i];
      // Reset le contenu de l'icône
      const oldIcon = slotEl.querySelector(".hotbar-icon");
      if (oldIcon) oldIcon.remove();
      const oldCd = slotEl.querySelector(".hotbar-cooldown");
      if (oldCd) oldCd.remove();

      if (slotDef) {
        slotEl.classList.remove("empty");
        const icon = document.createElement("div");
        icon.className = "hotbar-icon";
        if (slotDef.skillId === "blackball") {
          const ball = document.createElement("div");
          ball.className = "hotbar-blackball";
          icon.appendChild(ball);
        }
        slotEl.insertBefore(icon, slotEl.firstChild);
        const cd = document.createElement("div");
        cd.className = "hotbar-cooldown";
        slotEl.appendChild(cd);
      } else {
        slotEl.classList.add("empty");
      }
    }
  }

  saveHotbar() {
    if (!this.room) return;
    const payload: { [k: string]: string | null } = {};
    for (let i = 0; i < 8; i++) {
      payload[String(i)] = this.hotbarSlots[i]?.skillId || null;
    }
    (this.room as any).send(41, { slots: payload });
  }

  handleSkillCast(data: any) {
    if (data.skillId !== "blackball") return;
    const targetEntity = this.monsterEntities[data.targetMonsterId];
    const casterEntity = this.playerEntities[data.casterSessionId];
    if (!targetEntity || !casterEntity) return;

    // Crée le projectile (cercle noir avec halo violet)
    const projectile = this.add.circle(casterEntity.x, casterEntity.y - 24, 10, 0x1a0a2e, 1);
    projectile.setStrokeStyle(2, 0x8b3fdb, 0.9);
    projectile.setDepth(150);

    // Halo glow violet derrière le projectile
    const glow = this.add.circle(casterEntity.x, casterEntity.y - 24, 16, 0x6a1b9a, 0.4);
    glow.setDepth(149);

    // Tween : projectile vole vers la cible
    this.tweens.add({
      targets: [projectile, glow],
      x: targetEntity.x,
      y: targetEntity.y - 20,
      duration: 350,
      ease: "Cubic.easeIn",
      onComplete: () => {
        // Impact : flash blanc sur la cible + dégâts flottants + destruction du projectile
        targetEntity.setTint(0xffffff);
        this.time.delayedCall(100, () => targetEntity.clearTint());
        this.showFloatingDamage(targetEntity.x, targetEntity.y - 30, data.damage, "#b46aff");

        // Petit flash d'impact
        const impact = this.add.circle(targetEntity.x, targetEntity.y - 20, 24, 0x8b3fdb, 0.6);
        impact.setDepth(151);
        this.tweens.add({
          targets: impact,
          scale: 2,
          alpha: 0,
          duration: 250,
          onComplete: () => impact.destroy()
        });

        projectile.destroy();
        glow.destroy();
      }
    });
  }

  handleAttackHit(data: any) {
    if (data.attackerType === "player") {
      // Animation : flash sur le monstre + dégât flottant
      const monsterEntity = this.monsterEntities[data.targetMonsterId];
      if (monsterEntity) {
        monsterEntity.setTint(0xffffff);
        this.time.delayedCall(80, () => monsterEntity.clearTint());
        this.showFloatingDamage(monsterEntity.x, monsterEntity.y - 30, data.damage, "#ff6666");
      }
      // Animation lunge sur le joueur attaquant
      const playerEntity = this.playerEntities[data.attackerSessionId];
      if (playerEntity && monsterEntity) {
        const homeX = playerEntity.x;
        const homeY = playerEntity.y;
        const dx = monsterEntity.x - homeX;
        const dy = monsterEntity.y - homeY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > 0) {
          const lungeX = homeX + (dx / dist) * 8;
          const lungeY = homeY + (dy / dist) * 8;
          this.tweens.add({
            targets: playerEntity, x: lungeX, y: lungeY,
            duration: 100, yoyo: true, ease: "Cubic.easeOut"
          });
        }
      }
    } else if (data.attackerType === "monster") {
      // Animation : flash sur le joueur + dégât flottant
      const playerEntity = this.playerEntities[data.targetSessionId];
      if (playerEntity) {
        playerEntity.setTint(0xff6666);
        this.time.delayedCall(120, () => playerEntity.clearTint());
        this.showFloatingDamage(playerEntity.x, playerEntity.y - 30, data.damage, "#ff3030");
      }
    }
  }

  updateXpBar(level: number, xp: number, xpForNext: number) {
    const levelEl = document.getElementById("player-level-text");
    const fillEl = document.getElementById("player-xp-bar-fill");
    const textEl = document.getElementById("player-xp-bar-text");
    if (levelEl) levelEl.textContent = `Niv. ${level}`;
    if (fillEl) {
      const pct = xpForNext === Infinity || level >= 60 ? 100 : Math.max(0, Math.min(100, (xp / xpForNext) * 100));
      fillEl.style.width = `${pct}%`;
    }
    if (textEl) {
      textEl.textContent = level >= 60 ? "MAX" : `${xp} / ${xpForNext}`;
    }
  }

  showFloatingDamage(x: number, y: number, dmg: number, color: string) {
    const text = this.add.text(x, y, `-${dmg}`, {
      fontSize: "16px", fontFamily: "Arial", color, fontStyle: "bold",
      stroke: "#000000", strokeThickness: 3
    });
    text.setOrigin(0.5, 0.5);
    text.setDepth(200);
    this.tweens.add({
      targets: text,
      y: y - 40,
      alpha: 0,
      duration: 800,
      ease: "Cubic.easeOut",
      onComplete: () => text.destroy()
    });
  }

  update(_time: number, _delta: number) {
    const lerpFactor = 0.2;
    for (const sid in this.playerEntities) {
      const entity = this.playerEntities[sid];
      const state = this.playerStates[sid];
      const label = this.playerLabels[sid];
      const bubble = this.playerBubbles[sid];
      if (!entity || !state) continue;
      entity.x = Phaser.Math.Linear(entity.x, state.targetX, lerpFactor);
      entity.y = Phaser.Math.Linear(entity.y, state.targetY, lerpFactor);
      if (Math.abs(entity.x - state.targetX) < 0.5) entity.x = state.targetX;
      if (Math.abs(entity.y - state.targetY) < 0.5) entity.y = state.targetY;
      const labelOffset = state.charIndex === SAJA_CHAR_INDEX ? 165 : 50;
      const bubbleOffset = state.charIndex === SAJA_CHAR_INDEX ? 175 : 60;
      if (label) { label.x = entity.x; label.y = entity.y - labelOffset; }
      if (bubble) { bubble.x = entity.x; bubble.y = entity.y - bubbleOffset; }
    }

    // Met à jour la barre de HP du joueur (lit le state serveur)
    if (this.room && this.room.sessionId) {
      const myPlayer = (this.room.state as any)?.players?.get?.(this.room.sessionId);
      if (myPlayer) {
        const hp = myPlayer.hp || 0;
        const maxHp = myPlayer.maxHp || 100;
        const pct = Math.max(0, Math.min(100, (hp / maxHp) * 100));
        const fill = document.getElementById("player-hp-bar-fill");
        const text = document.getElementById("player-hp-bar-text");
        if (fill) fill.style.width = `${pct}%`;
        if (text) text.textContent = `${hp} / ${maxHp}`;
      }
    }

    const monstersState = (this.room?.state as any)?.monsters;
    for (const id in this.monsterEntities) {
      const entity = this.monsterEntities[id];
      const state = this.monsterStates[id];
      const label = this.monsterLabels[id];
      const bars = this.monsterHpBars[id];
      if (!entity || !state) continue;
      entity.x = Phaser.Math.Linear(entity.x, state.targetX, lerpFactor);
      entity.y = Phaser.Math.Linear(entity.y, state.targetY, lerpFactor);
      if (Math.abs(entity.x - state.targetX) < 0.5) entity.x = state.targetX;
      if (Math.abs(entity.y - state.targetY) < 0.5) entity.y = state.targetY;
      if (label) { label.x = entity.x; label.y = entity.y - 50; }

      // Dessine la barre de HP
      if (bars && monstersState) {
        const monster = monstersState.get(id);
        if (monster && monster.alive) {
          const barWidth = 36;
          const barHeight = 4;
          const barX = entity.x - barWidth / 2;
          const barY = entity.y - 60;
          const pct = Math.max(0, monster.hp / monster.maxHp);
          bars.bg.clear();
          bars.bg.fillStyle(0x000000, 0.7);
          bars.bg.fillRect(barX - 1, barY - 1, barWidth + 2, barHeight + 2);
          bars.fill.clear();
          const color = pct > 0.5 ? 0x2ecc71 : pct > 0.25 ? 0xf39c12 : 0xe74c3c;
          bars.fill.fillStyle(color, 1);
          bars.fill.fillRect(barX, barY, barWidth * pct, barHeight);
        }
      }
    }

    // Met à jour la position du marqueur de cible verrouillée
    if (this.targetMonsterId && this.monsterTargetMarker) {
      const targetEntity = this.monsterEntities[this.targetMonsterId];
      if (targetEntity) {
        this.monsterTargetMarker.setPosition(targetEntity.x, targetEntity.y - 4);
      }
    }

    this.updateNearbyNPC();

    if (!this.currentPlayer || !this.room) return;
    if (this.chatActive || this.currentDialogue || this.cardsOpen || this.statsOpen || this.friendsOpen) {
      this.inputPayload.left = false; this.inputPayload.right = false;
      this.inputPayload.up = false; this.inputPayload.down = false;
    } else {
      this.inputPayload.left = this.cursorKeys.left.isDown || this.zqsdKeys.Q.isDown;
      this.inputPayload.right = this.cursorKeys.right.isDown || this.zqsdKeys.D.isDown;
      this.inputPayload.up = this.cursorKeys.up.isDown || this.zqsdKeys.Z.isDown;
      this.inputPayload.down = this.cursorKeys.down.isDown || this.zqsdKeys.S.isDown;
    }
    this.room.send(0, this.inputPayload);

    // Auto-attack : envoie un message d'attaque toutes les ~1 sec si clic maintenu
    if (this.isAttacking && this.targetMonsterId) {
      const now = Date.now();
      if (now - this.lastAttackSent >= 1000) {
        this.lastAttackSent = now;
        this.room.send(30, { monsterId: this.targetMonsterId });
      }
    }

    // Mise à jour visuelle des cooldowns hotbar
    this.updateHotbarCooldowns();
  }

  updateNearbyNPC() {
    const hint = document.getElementById("interact-hint");
    if (!hint || !this.currentPlayer) return;
    if (this.cardsOpen) { hint.style.display = "none"; return; }
    let closestNPC: string | null = null;
    let closestDist = NPC_INTERACT_DIST;
    for (const sid in this.playerStates) {
      const state = this.playerStates[sid];
      if (!state.isNPC) continue;
      const entity = this.playerEntities[sid];
      if (!entity) continue;
      const dx = entity.x - this.currentPlayer.x;
      const dy = entity.y - this.currentPlayer.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < closestDist) { closestDist = dist; closestNPC = sid; }
    }
    this.nearbyNPC = closestNPC;
    if (closestNPC && !this.currentDialogue) hint.style.display = "block";
    else hint.style.display = "none";
  }
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

// =====================================================================
// AUTH SUPABASE
// =====================================================================

let gameStarted = false;
let currentMode: "login" | "signup" = "login";

function showAuthError(msg: string) {
  document.getElementById("auth-error")!.textContent = msg;
}

function setAuthLoading(loading: boolean) {
  (document.getElementById("auth-submit") as HTMLButtonElement).disabled = loading;
  document.getElementById("auth-loading")!.style.display = loading ? "block" : "none";
}

type StrengthResult = {
  level: 0 | 1 | 2 | 3 | 4;
  label: string;
  crits: { length: boolean; upper: boolean; lower: boolean; digit: boolean; special: boolean };
};

function evaluatePasswordStrength(pwd: string): StrengthResult {
  const crits = {
    length: pwd.length >= 8,
    upper: /[A-Z]/.test(pwd),
    lower: /[a-z]/.test(pwd),
    digit: /[0-9]/.test(pwd),
    special: /[^A-Za-z0-9]/.test(pwd)
  };
  if (pwd.length === 0) return { level: 0, label: "", crits };
  if (!crits.length) return { level: 1, label: "Faible", crits };
  const baseCount =
    (crits.upper ? 1 : 0) + (crits.lower ? 1 : 0) +
    (crits.digit ? 1 : 0) + (crits.special ? 1 : 0);
  let level: 0 | 1 | 2 | 3 | 4;
  let label: string;
  if (baseCount <= 1) { level = 1; label = "Faible"; }
  else if (baseCount === 2) { level = 2; label = "Moyen"; }
  else if (baseCount === 3) { level = 3; label = "Bon"; }
  else {
    if (pwd.length >= 12) { level = 4; label = "Fort"; }
    else { level = 3; label = "Bon"; }
  }
  return { level, label, crits };
}

function setCritState(id: string, met: boolean) {
  const el = document.getElementById(id);
  if (!el) return;
  const icon = el.querySelector(".crit-icon")!;
  icon.textContent = met ? "✓" : "✗";
  el.className = met ? "crit-met" : "crit-unmet";
}

function updatePasswordStrengthUI() {
  const pwd = (document.getElementById("auth-password") as HTMLInputElement).value;
  const { level, label, crits } = evaluatePasswordStrength(pwd);

  const segs = document.querySelectorAll(".strength-segment");
  segs.forEach((s, i) => {
    s.className = "strength-segment";
    if (i < level) s.classList.add(`active-${level}`);
  });
  const labelEl = document.getElementById("strength-label")!;
  labelEl.textContent = label;
  labelEl.className = "strength-label" + (level > 0 ? ` level-${level}` : "");

  setCritState("crit-length", crits.length);
  setCritState("crit-upper", crits.upper);
  setCritState("crit-lower", crits.lower);
  setCritState("crit-digit", crits.digit);
  setCritState("crit-special", crits.special);

  updateConfirmUI();
  updateSubmitButtonState();
}

function updateConfirmUI() {
  const pwd = (document.getElementById("auth-password") as HTMLInputElement).value;
  const confirmInput = document.getElementById("auth-password-confirm") as HTMLInputElement;
  const confirm = confirmInput.value;
  const indicator = document.getElementById("match-indicator")!;

  if (confirm.length === 0) {
    indicator.textContent = "";
    indicator.className = "match-indicator";
    confirmInput.classList.remove("match-ok", "match-fail");
  } else if (pwd === confirm) {
    indicator.textContent = "✓ Les mots de passe correspondent";
    indicator.className = "match-indicator match-ok";
    confirmInput.classList.add("match-ok");
    confirmInput.classList.remove("match-fail");
  } else {
    indicator.textContent = "✗ Les mots de passe ne correspondent pas";
    indicator.className = "match-indicator match-fail";
    confirmInput.classList.add("match-fail");
    confirmInput.classList.remove("match-ok");
  }
  updateSubmitButtonState();
}

function updateSubmitButtonState() {
  const submitBtn = document.getElementById("auth-submit") as HTMLButtonElement;
  const email = (document.getElementById("auth-email") as HTMLInputElement).value.trim();
  const pwd = (document.getElementById("auth-password") as HTMLInputElement).value;

  if (currentMode === "login") {
    submitBtn.disabled = !email || !pwd;
    return;
  }
  const confirm = (document.getElementById("auth-password-confirm") as HTMLInputElement).value;
  const pseudo = (document.getElementById("auth-pseudo") as HTMLInputElement).value.trim();
  const { level } = evaluatePasswordStrength(pwd);

  const valid = !!email && level >= 2 && pwd === confirm && pseudo.length >= 3;
  submitBtn.disabled = !valid;
}

function switchMode(mode: "login" | "signup") {
  currentMode = mode;
  const tabLogin = document.getElementById("tab-login")!;
  const tabSignup = document.getElementById("tab-signup")!;
  const pseudoInput = document.getElementById("auth-pseudo")!;
  const confirmInput = document.getElementById("auth-password-confirm")!;
  const strengthContainer = document.getElementById("strength-container")!;
  const criteriaList = document.getElementById("criteria-list")!;
  const matchIndicator = document.getElementById("match-indicator")!;
  const submitBtn = document.getElementById("auth-submit")!;
  const passwordInput = document.getElementById("auth-password") as HTMLInputElement;

  if (mode === "login") {
    tabLogin.classList.add("active");
    tabSignup.classList.remove("active");
    pseudoInput.style.display = "none";
    confirmInput.style.display = "none";
    strengthContainer.style.display = "none";
    criteriaList.style.display = "none";
    matchIndicator.style.display = "none";
    submitBtn.textContent = "Se connecter";
    passwordInput.setAttribute("autocomplete", "current-password");
  } else {
    tabSignup.classList.add("active");
    tabLogin.classList.remove("active");
    pseudoInput.style.display = "block";
    confirmInput.style.display = "block";
    strengthContainer.style.display = "block";
    criteriaList.style.display = "block";
    matchIndicator.style.display = "block";
    submitBtn.textContent = "Créer mon compte";
    passwordInput.setAttribute("autocomplete", "new-password");
    updatePasswordStrengthUI();
  }
  showAuthError("");
  updateSubmitButtonState();
}

async function handleAuth() {
  const email = (document.getElementById("auth-email") as HTMLInputElement).value.trim();
  const password = (document.getElementById("auth-password") as HTMLInputElement).value;
  const pseudo = (document.getElementById("auth-pseudo") as HTMLInputElement).value.trim();
  const confirm = (document.getElementById("auth-password-confirm") as HTMLInputElement).value;

  if (!email || !password) {
    showAuthError("Email et mot de passe requis");
    return;
  }

  if (currentMode === "signup") {
    if (!pseudo || pseudo.length < 3) {
      showAuthError("Pseudo de 3 caractères minimum");
      return;
    }
    const { level } = evaluatePasswordStrength(password);
    if (level < 2) {
      showAuthError("Mot de passe trop faible (minimum : 8 caractères + 2 critères)");
      return;
    }
    if (password !== confirm) {
      showAuthError("Les mots de passe ne correspondent pas");
      return;
    }
  }

  setAuthLoading(true);
  showAuthError("");

  try {
    if (currentMode === "signup") {
      const charIndex = Math.floor(Math.random() * 8);

      // Vérifier d'abord que le pseudo n'est pas pris (pour message d'erreur clair)
      const { data: existing } = await supabase
        .from("profiles")
        .select("id")
        .eq("pseudo", pseudo)
        .maybeSingle();
      if (existing) {
        showAuthError("Ce pseudo est déjà pris");
        setAuthLoading(false);
        return;
      }

      // Créer le compte auth — le trigger côté DB créera profile + stats automatiquement
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            pseudo,
            char_index: charIndex
          }
        }
      });
      if (error) {
        if (error.message.toLowerCase().includes("already registered")) {
          showAuthError("Cet email est déjà utilisé");
        } else {
          showAuthError(error.message);
        }
        setAuthLoading(false);
        return;
      }
      if (!data.user) { showAuthError("Erreur création de compte"); setAuthLoading(false); return; }

      // Le trigger a créé le profile, mais on attend que la session soit active
      // (signUp avec confirmation OFF donne directement une session)
      onAuthSuccess(data.user.id, pseudo, charIndex);
    } else {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) { showAuthError("Email ou mot de passe incorrect"); setAuthLoading(false); return; }
      if (!data.user) { showAuthError("Erreur de connexion"); setAuthLoading(false); return; }

      const { data: profile, error: profErr } = await supabase
        .from("profiles")
        .select("pseudo, char_index, role")
        .eq("id", data.user.id)
        .single();
      if (profErr || !profile) {
        showAuthError("Profil introuvable. Contacte un admin.");
        setAuthLoading(false);
        return;
      }

      onAuthSuccess(data.user.id, profile.pseudo, profile.char_index, profile.role);
    }
  } catch (err: any) {
    showAuthError("Erreur : " + (err?.message || err));
    setAuthLoading(false);
  }
}

function onAuthSuccess(userId: string, pseudo: string, charIndex: number, role: string = "player") {
  // Affiche le loading screen IMMÉDIATEMENT et masque l'auth
  document.getElementById("auth-screen")!.style.display = "none";
  showLoadingScreen();
  setLoadingProgress(5, "Initialisation...");

  // Le game-container reste caché derrière le loading screen jusqu'à ce que tout soit prêt
  document.getElementById("game-container")!.style.display = "block";

  localStorage.setItem("user_id", userId);
  localStorage.setItem("pseudo", pseudo);
  localStorage.setItem("char_index", charIndex.toString());
  localStorage.setItem("role", role);

  if (!gameStarted) {
    gameStarted = true;
    startGame();
  }
}

function startGame() {
  new Phaser.Game({
    type: Phaser.AUTO,
    width: window.innerWidth,
    height: window.innerHeight,
    backgroundColor: "#000000",
    parent: "game",
    pixelArt: true,
    physics: { default: "arcade" },
    scale: {
      mode: Phaser.Scale.RESIZE,
      autoCenter: Phaser.Scale.CENTER_BOTH
    },
    scene: [GameScene]
  });
}

// Setup des handlers de l'écran de login
document.getElementById("tab-login")?.addEventListener("click", () => switchMode("login"));
document.getElementById("tab-signup")?.addEventListener("click", () => switchMode("signup"));
document.getElementById("auth-submit")?.addEventListener("click", () => handleAuth());

// Réactivité des champs
document.getElementById("auth-email")?.addEventListener("input", () => updateSubmitButtonState());
document.getElementById("auth-password")?.addEventListener("input", () => {
  if (currentMode === "signup") updatePasswordStrengthUI();
  else updateSubmitButtonState();
});
document.getElementById("auth-password-confirm")?.addEventListener("input", () => updateConfirmUI());
document.getElementById("auth-pseudo")?.addEventListener("input", () => updateSubmitButtonState());

// Submit avec Entrée
["auth-email", "auth-password", "auth-password-confirm", "auth-pseudo"].forEach(id => {
  document.getElementById(id)?.addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key === "Enter") {
      const btn = document.getElementById("auth-submit") as HTMLButtonElement;
      if (!btn.disabled) handleAuth();
    }
  });
});

// État initial
updateSubmitButtonState();

// Vérifier si déjà connecté
(async () => {
  const { data } = await supabase.auth.getSession();
  if (data.session?.user) {
    const { data: profile } = await supabase
      .from("profiles")
      .select("pseudo, char_index, role")
      .eq("id", data.session.user.id)
      .single();
    if (profile) {
      onAuthSuccess(data.session.user.id, profile.pseudo, profile.char_index, profile.role);
    }
  }
})();